import { CSSProperties, useEffect, useMemo } from "react";
import style from "./monday-task-column-picker.module.css";
import { DropdownButton as DropdownButtonStandlone } from "frontend/canvas-designer-new/elements-toolbar/toolbar-buttons";
import {
  DropdownButton as DropdownButtonCIM,
  Icon,
} from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/toolbar-buttons";
import { MondayTaskElement } from "shared/datamodel/schemas/monday-task";
import { ColumnVisibilityIcon } from "../../components/monday-task-icons";
import { COLUMN_BLACKLIST } from "../../consts";
import { calculatePinnedColumns } from "../../utils";
import { MondayTaskColumnPickerRow } from "./monday-task-column-picker-row";
import { useMondayItem } from "frontend/canvas-designer-new/integrations/hooks/use-monday-item";
import { MondayItemData } from "shared/integrations/monday-types";
import { MondayItemColumn } from "elements/monday-task/controller";
import { useIntegrationsProvider } from "frontend/hooks/use-integrations-provider";
import { MondayColumn, MondayColumnInMonday } from "shared/integrations/mapper";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { useIsCIMReorderEnabled } from "utils/url-utils";

interface MondayTaskColumnPickerProps {
  element: MondayTaskElement;
  documentId?: string;
  onChange?: (
    value: { hiddenColumns?: string[]; pinnedColumns?: string[] },
    addUndo: boolean,
    metadata?: { shouldExpand?: boolean; shouldCollapse?: boolean }
  ) => void;
}

function hasExpandableContent(
  item: MondayItemData | undefined,
  columns: MondayColumn[],
  hiddenColumns: Set<string>,
  pinnedColumns: Set<string>,
  hiddenFiles: string[]
): boolean {
  if (!item) return false;

  const hasUpdates = item.updates.length > 0;
  if (hasUpdates) return true;

  const hasDetailColumns = columns.some(
    (col) => !pinnedColumns.has(col.id) && !hiddenColumns.has(col.id) && !COLUMN_BLACKLIST.has(col.type)
  );
  if (hasDetailColumns) return true;

  const fileColumns = item.columnValues?.filter((col: MondayItemColumn) => col.type === "file") ?? [];
  const allFiles = fileColumns.flatMap((col: MondayItemColumn) => col.value || []) as Array<{ id: string }>;
  const hiddenFilesSet = new Set(hiddenFiles ?? []);
  const visibleFiles = allFiles.filter((file): file is { id: string } => !hiddenFilesSet.has(file.id));

  return visibleFiles.length > 0;
}

export function MondayTaskColumnPicker({ element, onChange }: MondayTaskColumnPickerProps) {
  const {
    hiddenColumns = [],
    pinnedColumns: _pinnedColumns,
    integrationId,
    mondayItemId,
    expanded = false,
    hiddenFiles = [],
  } = element;

  const { data: item } = useMondayItem(mondayItemId);
  const { getBoardIntegrationConfig, loadItems } = useIntegrationsProvider();

  const isCIM = useIsCIMReorderEnabled();
  const newFloatersStyle = useFeatureFlag("cim-new-floaters-style");
  const newStyle = isCIM && newFloatersStyle;

  // Load item in integrations provider -> Ensures board columns are available.
  // TODO: This is legacy code and we should make a useMondayBoard or something...
  useEffect(() => {
    if (integrationId && mondayItemId) {
      loadItems({ [integrationId]: [mondayItemId] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, mondayItemId]);

  const integrationConfig = getBoardIntegrationConfig(integrationId);

  const boardColumns: MondayColumn[] =
    integrationConfig?.columns?.map((c: MondayColumnInMonday) => ({
      ...c,
      settings: JSON.parse(c.settings_str ?? "null"),
    })) ?? [];

  const memoizedBoardcolumns = useDeepEqualMemo(boardColumns);

  const itemColumns = useMemo(() => {
    return memoizedBoardcolumns.map((col) => {
      const value = item?.columnValues?.find((c) => c.id === col.id)?.value;
      return { ...col, value };
    });
  }, [memoizedBoardcolumns, item?.columnValues]);

  const pinnedColumns = useMemo(() => {
    if (itemColumns.length === 0) return [];
    const pinnedCols = calculatePinnedColumns(itemColumns, _pinnedColumns);
    return pinnedCols.map((col) => col.id);
  }, [itemColumns, _pinnedColumns]);

  const filteredPinnedColumns = useMemo(() => {
    if (itemColumns.length === 0) return new Set();
    return new Set(pinnedColumns.filter((col) => itemColumns.some((c) => c.id === col)));
  }, [pinnedColumns, itemColumns]);

  const filteredHiddenColumns = useMemo(() => {
    if (!memoizedBoardcolumns) return new Set();
    return new Set(hiddenColumns.filter((col) => memoizedBoardcolumns.some((c) => c.id === col)));
  }, [hiddenColumns, memoizedBoardcolumns]);

  const allowPins = useMemo(() => {
    if (itemColumns.length === 0) return "none";
    if (pinnedColumns.length >= 3) return "none";
    if (pinnedColumns.length < 2) return "all";

    const pinnedPeopleColumn = pinnedColumns.find((col) =>
      itemColumns.some((c) => c.id === col && c.type === "people")
    );

    return pinnedPeopleColumn ? "all" : "people-only";
  }, [pinnedColumns, itemColumns]);

  const displayableColumns = useMemo(() => {
    const allColumns = itemColumns.filter((col) => !COLUMN_BLACKLIST.has(col.type));
    const pinnedColumnIdsSet = filteredPinnedColumns;

    const pinnedCols: typeof allColumns = [];
    const nonPinnedCols: typeof allColumns = [];

    for (const pinnedId of pinnedColumns) {
      const col = allColumns.find((c) => c.id === pinnedId);
      if (col) {
        pinnedCols.push(col);
      }
    }

    for (const col of allColumns) {
      if (!pinnedColumnIdsSet.has(col.id)) {
        nonPinnedCols.push(col);
      }
    }

    // Pinned columns always at the top.
    return [...pinnedCols, ...nonPinnedCols];
  }, [itemColumns, pinnedColumns, filteredPinnedColumns]);

  const handleToggleColumn = (columnId: string) => {
    const hidden = new Set(hiddenColumns);
    const pinned = new Set(pinnedColumns);
    const wasPinned = pinned.has(columnId);

    if (hidden.has(columnId)) {
      hidden.delete(columnId);
    } else {
      hidden.add(columnId);
      if (pinned.has(columnId)) {
        pinned.delete(columnId);
      }
    }

    const hasContent = hasExpandableContent(item, memoizedBoardcolumns, hidden, pinned, hiddenFiles);
    const shouldExpand = !expanded && !wasPinned && hasContent;
    const shouldCollapse = expanded && !hasContent;

    onChange?.({ hiddenColumns: [...hidden], pinnedColumns: [...pinned] }, true, {
      shouldExpand,
      shouldCollapse,
    });
  };

  const handleTogglePinnedColumn = (columnId: string) => {
    const pinned = new Set(pinnedColumns);
    if (pinned.has(columnId)) {
      pinned.delete(columnId);
    } else {
      pinned.add(columnId);
    }
    const hidden = new Set(hiddenColumns);
    if (!pinnedColumns.includes(columnId)) {
      hidden.delete(columnId);
    }

    onChange?.({ hiddenColumns: [...hidden], pinnedColumns: [...pinned] }, true);
  };

  const renderPicker = () => {
    return (
      <div
        className={style.popup}
        style={
          newStyle
            ? ({ "--bg-color": "#1e1e1e", "--border-radius": "10px", "--text-color": "#C8CCD8" } as CSSProperties)
            : undefined
        }
      >
        <div className={style.title}>Display data on your task</div>
        <div className={style.columnList}>
          {displayableColumns.map((column) => {
            const isHidden = filteredHiddenColumns.has(column.id);
            const isPinned = filteredPinnedColumns.has(column.id);
            const allowPin = allowPins === "all" || (allowPins === "people-only" && column.type === "people");

            return (
              <MondayTaskColumnPickerRow
                key={column.id}
                column={column}
                isHidden={isHidden}
                isPinned={isPinned}
                allowPin={allowPin}
                onToggle={handleToggleColumn}
                onPin={handleTogglePinnedColumn}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const DropdownButton = newStyle ? DropdownButtonCIM : DropdownButtonStandlone;

  return (
    <DropdownButton
      tooltip="Columns"
      renderPopup={renderPicker}
      data-testid="MondayTaskColumnPicker"
      className={style.container}
      hideArrow
    >
      <div className={style.dropdownIcon}>{newStyle ? <Icon icon="icon-select-data" /> : <ColumnVisibilityIcon />}</div>
    </DropdownButton>
  );
}
